import { inject, Injectable, signal } from '@angular/core';
import { IPagerSettings, ITipoFluxoNomoh } from '@nomoh-monorepo/shared-types';
import { FeatureFluxoService } from './feature-fluxo.service';

@Injectable({
  providedIn: 'root',
})
export class FeatureFluxoDataService {
  private httpService = inject(FeatureFluxoService);

  // Signals privados
  private _fluxos = signal<ITipoFluxoNomoh[]>([]);
  private _fluxoAtual = signal<ITipoFluxoNomoh | null>(null);
  private _loading = signal(false);
  private _totalFluxos = signal(0);

  // Signals públicos (readonly)
  $fluxos = this._fluxos.asReadonly();
  $fluxoAtual = this._fluxoAtual.asReadonly();
  $loading = this._loading.asReadonly();
  $totalFluxos = this._totalFluxos.asReadonly();

  async loadFluxos(
    filtro: Record<string, unknown> = {},
    pagerSettings: IPagerSettings = { offSet: 0, limit: 100 }
  ) {
    try {
      this._loading.set(true);
      const response = await this.httpService.findAll(filtro, pagerSettings);
      this._fluxos.set(response.data);
      this._totalFluxos.set(response.pagerSettings?.total || 0);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    } finally {
      this._loading.set(false);
    }
  }

  async loadFluxoById(id: string) {
    try {
      this._loading.set(true);
      const fluxo = await this.httpService.findById(id);
      this._fluxoAtual.set(fluxo);
      return fluxo;
    } catch (error) {
      console.error('Erro ao carregar fluxo:', error);
      return null;
    } finally {
      this._loading.set(false);
    }
  }

  async createFluxo(data: Partial<ITipoFluxoNomoh>) {
    try {
      this._loading.set(true);
      const id = await this.httpService.create(data);
      await this.loadFluxos(); // Recarrega lista
      return id;
    } catch (error) {
      console.error('Erro ao criar fluxo:', error);
      return null;
    } finally {
      this._loading.set(false);
    }
  }

  async updateFluxo(id: string, data: Partial<ITipoFluxoNomoh>) {
    try {
      this._loading.set(true);
      await this.httpService.update(id, data);

      // Atualiza fluxo atual se for o mesmo
      if (this._fluxoAtual()?.id === id) {
        const fluxoAtualizado = await this.httpService.findById(id);
        this._fluxoAtual.set(fluxoAtualizado);
      }

      await this.loadFluxos(); // Recarrega lista
      return true;
    } catch (error) {
      console.error('Erro ao atualizar fluxo:', error);
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  async deleteFluxo(id: string) {
    try {
      this._loading.set(true);
      await this.httpService.delete(id);

      // Limpa fluxo atual se for o mesmo
      if (this._fluxoAtual()?.id === id) {
        this._fluxoAtual.set(null);
      }

      await this.loadFluxos(); // Recarrega lista
      return true;
    } catch (error) {
      console.error('Erro ao excluir fluxo:', error);
      return false;
    } finally {
      this._loading.set(false);
    }
  }

  setFluxoAtual(fluxo: ITipoFluxoNomoh | null) {
    this._fluxoAtual.set(fluxo);
  }

  clearFluxoAtual() {
    this._fluxoAtual.set(null);
  }

  /**
   * Busca os nodes de um workflow N8N
   * @param workflowId ID do workflow no N8N
   * @returns Lista de nodes simplificados com formato $('Node Name')
   */
  async loadWorkflowNodes(workflowId: string) {
    try {
      this._loading.set(true);
      const nodes = await this.httpService.getWorkflowNodes(workflowId);
      return nodes;
    } catch (error) {
      console.error('Erro ao carregar nodes do N8N:', error);
      return [];
    } finally {
      this._loading.set(false);
    }
  }
}
